<script setup lang="ts">
import ArrowRight from '@/assets/icon/arrow-right.svg'
const matomo = useMatomo()
type ExposureCard = {
  id: number
  exchange: string
  logo: string
  logoBit64?: string
  title: string
  location: string
  surveyDate: string
  urlPath: string
  surveyId: string
  isExhibition: boolean
  images: string[]
  imagesBit1000: string[]
  color?: string
  riskLabel?: string
}

type ExposureImageItem = string | { list?: unknown; detail?: unknown; url?: unknown }

const { t, localePath } = useAppI18n()
const route = useRoute()
const { isRtl, dir } = useRtl()
const { setBreadcrumb } = useLsBreadcrumb()

const batchSize = 3
const maxExposureCards = 15
const maxSlideImages = 5
const props = defineProps<{
  data?: Record<string, unknown>
}>()

const exposureCards = ref<ExposureCard[]>([])
const currentPage = ref(0)
const activeCardId = ref(exposureCards.value[0]?.id ?? 0)
const hoveredCardId = ref<number | null>(null)
const activeImageMap = reactive<Record<number, number>>({})

const getExposureImageSrc = (image: ExposureImageItem) => {
  if (typeof image === 'string') {
    return image
  }

  return (
    [image.list, image.detail, image.url].find((src): src is string => typeof src === 'string' && src.length > 0) || ''
  )
}

const getStringField = (record: Record<string, unknown>, field: string) => {
  const value = record[field]
  return typeof value === 'string' ? value.trim() : ''
}

/** 首页实勘卡片按 3 张一组展示，不足 15 条时裁剪到完整分组数量。 */
const getDisplayExposureCards = (cards: ExposureCard[]) => {
  const displayCount =
    cards.length < maxExposureCards ? Math.floor(cards.length / batchSize) * batchSize : maxExposureCards

  return cards.slice(0, displayCount)
}

const mapExposureCardsFromApi = (payload: Record<string, unknown> | undefined): ExposureCard[] => {
  const cards = payload?.cards

  if (!Array.isArray(cards)) {
    return []
  }

  return cards
    .map((item, index) => {
      if (!item || typeof item !== 'object') {
        return null
      }

      const record = item as Record<string, unknown>
      const trader = (record.trader as Record<string, unknown> | undefined) ?? {}
      const country = typeof record.country === 'string' ? record.country : ''
      const city = typeof record.city === 'string' ? record.city : ''
      const location = [country, city].filter(Boolean).join(', ')
      const images = Array.isArray(record.images)
        ? record.images.map((image) => getExposureImageSrc(image as ExposureImageItem)).filter(Boolean)
        : []
      const slideImages = images.slice(0, maxSlideImages)
      const imagesBit1000 = Array.isArray(record.imagesBit1000)
        ? record.imagesBit1000.map((image) => getExposureImageSrc(image as ExposureImageItem)).filter(Boolean)
        : []
      // 高分辨率图片缺失时回退到默认图片，避免整张卡片没有背景图。
      const slideImagesBit1000 = (imagesBit1000.length > 0 ? imagesBit1000 : images).slice(0, maxSlideImages)
      const urlPath = getStringField(record, 'urlPath')
      const surveyId = getStringField(record, 'surveyId')

      return {
        id: index + 1,
        exchange: typeof trader.name === 'string' && trader.name ? trader.name : 'Unknown',
        logo: typeof trader.logo === 'string' ? trader.logo : '',
        logoBit64: typeof trader.logoBit64 === 'string' ? trader.logoBit64 : '',
        title: typeof record.title === 'string' ? record.title : '',
        location: location || 'Unknown',
        surveyDate: (typeof record.surveyDate === 'string' && record.surveyDate) || '',
        urlPath,
        surveyId,
        isExhibition: Boolean(record.isexhibition),
        images: slideImages,
        imagesBit1000: slideImagesBit1000,
        color: typeof record.color === 'string' ? record.color : 'red',
        riskLabel: typeof record.riskLabel === 'string' ? record.riskLabel : '',
      } satisfies ExposureCard
    })
    .filter((item): item is ExposureCard => item !== null && item.imagesBit1000.length > 0 && Boolean(item.urlPath))
}

const syncExposureData = () => {
  const payload = props.data as Record<string, unknown> | undefined
  const mappedCards = getDisplayExposureCards(mapExposureCardsFromApi(payload))

  exposureCards.value = mappedCards
  currentPage.value = 0
  activeCardId.value = exposureCards.value[0]?.id ?? 0
  hoveredCardId.value = null
  clearAutoPlay()

  Object.keys(activeImageMap).forEach((key) => {
    delete activeImageMap[Number(key)]
  })
  for (const card of exposureCards.value) {
    activeImageMap[card.id] = 0
  }
}

const pagedCards = computed(() => {
  const pages: ExposureCard[][] = []

  for (let index = 0; index < exposureCards.value.length; index += batchSize) {
    pages.push(exposureCards.value.slice(index, index + batchSize))
  }

  return pages
})

const totalPages = computed(() => pagedCards.value.length)
const canGoPrev = computed(() => currentPage.value > 0)
const canGoNext = computed(() => currentPage.value < totalPages.value - 1)
let autoPlayTimer: ReturnType<typeof setInterval> | null = null

const clearAutoPlay = () => {
  if (!autoPlayTimer) {
    return
  }

  clearInterval(autoPlayTimer)
  autoPlayTimer = null
}

watch(
  () => props.data,
  () => {
    syncExposureData()
  },
  { immediate: true },
)

const getCardById = (cardId: number) => exposureCards.value.find((card) => card.id === cardId)

const startAutoPlay = (cardId: number) => {
  const card = getCardById(cardId)

  clearAutoPlay()

  if (!card || card.imagesBit1000.length <= 1) {
    return
  }

  autoPlayTimer = setInterval(() => {
    activeImageMap[cardId] = (activeImageMap[cardId] + 1) % card.imagesBit1000.length
  }, 3000)
}

const setPageFirstCardActive = () => {
  const firstCard = pagedCards.value[currentPage.value]?.[0]

  if (firstCard) {
    activeCardId.value = firstCard.id
  }
}

const toPrevBatch = () => {
  if (!canGoPrev.value) {
    return
  }
  matomo.trackEvent('home_survey_switch', {})
  currentPage.value -= 1
  hoveredCardId.value = null
  clearAutoPlay()
  setPageFirstCardActive()
}

const toNextBatch = () => {
  if (!canGoNext.value) {
    return
  }
  matomo.trackEvent('home_survey_switch', {})
  currentPage.value += 1
  hoveredCardId.value = null
  clearAutoPlay()
  setPageFirstCardActive()
}

const onCardEnter = (cardId: number) => {
  hoveredCardId.value = cardId
  activeCardId.value = cardId
  startAutoPlay(cardId)
}

const onCardLeave = () => {
  hoveredCardId.value = null
  clearAutoPlay()
}

const toCardSlide = (cardId: number, index: number) => {
  activeImageMap[cardId] = index
}

const onDotEnter = (cardId: number, index: number) => {
  toCardSlide(cardId, index)
  clearAutoPlay()
}

const onDotLeave = (cardId: number) => {
  if (hoveredCardId.value !== cardId) {
    return
  }

  startAutoPlay(cardId)
}

/** 确保每个分页内始终有一张展开，避免翻页过渡时当前页三张同时收起。 */
const resolvePageActiveCardId = (pageCards: ExposureCard[]) => {
  return pageCards.some((card) => card.id === activeCardId.value) ? activeCardId.value : (pageCards[0]?.id ?? 0)
}

const isCardExpanded = (cardId: number, pageCards: ExposureCard[]) => cardId === resolvePageActiveCardId(pageCards)

const getDetailRouteName = (card: ExposureCard) => {
  return card.isExhibition ? 'inspections-expo-exchange-slug' : 'inspections-exchange-slug'
}

const getDetailRoute = (card: ExposureCard) => {
  return localePath({
    name: getDetailRouteName(card),
    params: {
      slug: card.urlPath,
    },
  })
}

const setDetailBreadcrumb = (card: ExposureCard) => {
  matomo.trackEvent('home_survey_card', {
    af_title: card.title,
    af_code: card.surveyId,
  })
  setBreadcrumb(card.urlPath, {
    labelKey: '82521',
    route: route.fullPath,
  })
}

onBeforeUnmount(() => {
  clearAutoPlay()
})
</script>

<template>
  <section v-if="exposureCards.length > 0" class="exposure" :dir="dir" aria-label="Sky Eye On-Site Inspection">
    <div class="exposure-header">
      <h2 class="exposure-title">{{ t('82521') }}</h2>
      <div class="exposure-actions">
        <button
          type="button"
          class="action-btn action-btn-prev"
          :class="{ 'is-disabled': !canGoPrev }"
          :disabled="!canGoPrev"
          aria-label="Previous"
          @click="toPrevBatch"
        >
          <ArrowRight class="action-icon size-[24px] [&_path]:!fill-white" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="action-btn"
          :class="{ 'is-disabled': !canGoNext }"
          :disabled="!canGoNext"
          aria-label="Next"
          @click="toNextBatch"
        >
          <ArrowRight class="action-icon [&_path]:!fill-white" aria-hidden="true" />
        </button>
      </div>
    </div>

    <div class="exposure-viewport">
      <div
        class="exposure-track"
        :style="{
          transform: `translateX(${isRtl ? '' : '-'}${currentPage * 100}%)`,
        }"
      >
        <div v-for="(pageCards, pageIndex) in pagedCards" :key="`page-${pageIndex}`" class="exposure-page">
          <NuxtLink
            v-for="card in pageCards"
            :key="card.id"
            :to="getDetailRoute(card)"
            target="_blank"
            class="exposure-card"
            :class="{
              'is-expanded': isCardExpanded(card.id, pageCards),
              'is-collapsed': !isCardExpanded(card.id, pageCards),
            }"
            @mouseenter="onCardEnter(card.id)"
            @mouseleave="onCardLeave"
            @click="setDetailBreadcrumb(card)"
          >
            <div class="card-image-stack">
              <CommonImage
                v-for="(image, imageIndex) in card.imagesBit1000"
                :key="imageIndex"
                :src="image"
                alt=""
                class="card-image size-full"
                :class="{ 'is-active': imageIndex === activeImageMap[card.id] }"
                loading="eager"
              />
            </div>
            <div class="card-gradient"></div>
            <div class="card-info">
              <div class="card-tags">
                <div v-if="card.riskLabel" class="danger-tag" :style="{ backgroundColor: card.color || 'red' }">
                  {{ card.riskLabel }}
                </div>
                <div class="exchange-tag">
                  <CommonImage
                    :src="card.logoBit64 || card.logo"
                    :alt="card.title"
                    class="exchange-logo"
                    loading="eager"
                    object-fit="cover"
                  />
                  <span>{{ card.exchange }}</span>
                </div>
              </div>
              <h3 class="card-title">{{ card.title }}</h3>
              <div class="card-meta">
                <div class="meta-item">
                  <span>{{ card.location }}</span>
                </div>
                <div class="meta-item is-time">
                  <span>{{ t('84279') }}</span>
                  <span>{{ card.surveyDate }}</span>
                </div>
              </div>
            </div>

            <div
              v-if="card.imagesBit1000.length > 1"
              class="slider-dots"
              role="tablist"
              aria-label="Exposure slide pagination"
              :class="{ 'is-visible': hoveredCardId === card.id }"
            >
              <button
                v-for="(_, index) in card.imagesBit1000"
                :key="index"
                type="button"
                class="dot"
                :class="{ active: index === activeImageMap[card.id] }"
                :aria-label="`Go to slide ${index + 1}`"
                :aria-selected="index === activeImageMap[card.id]"
                role="tab"
                @click.stop="toCardSlide(card.id, index)"
                @mouseenter.stop="onDotEnter(card.id, index)"
                @mouseleave.stop="onDotLeave(card.id)"
              ></button>
            </div>
          </NuxtLink>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="less">
.exposure {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 0;
  display: flex;
  flex-direction: column;
  gap: 30px;

  .exposure-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .exposure-title {
      color: #fff;
      font-size: 24px;
      font-weight: 600;
      line-height: 32px;
      margin: 0;
      text-align: start;
    }

    .exposure-actions {
      display: flex;
      gap: 20px;

      .action-btn {
        width: 40px;
        height: 40px;
        border: none;
        border-radius: 12px;
        background: rgba(227, 228, 242, 0.12);
        padding: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition:
          background-color 0.2s ease,
          opacity 0.2s ease;

        &:hover {
          background: rgba(227, 228, 242, 0.2);
        }

        &.is-disabled {
          cursor: not-allowed;
          opacity: 0.45;

          &:hover {
            background: rgba(227, 228, 242, 0.12);
          }
        }

        .action-icon {
          width: 24px;
          height: 24px;
        }

        &.action-btn-prev {
          transform: rotate(180deg);
        }

        [dir='rtl'] &.action-btn-prev {
          transform: rotate(0deg);
        }

        [dir='rtl'] &:not(.action-btn-prev) {
          transform: rotate(180deg);
        }
      }
    }
  }

  .exposure-viewport {
    width: 100%;
    overflow: hidden;
    position: relative;

    .exposure-track {
      display: flex;
      transition: transform 0.35s ease;
    }

    .exposure-page {
      flex: 0 0 100%;
      display: flex;
      align-items: center;
      gap: 24px;
      position: relative;
      z-index: 2;
    }
  }

  .exposure-card {
    height: 452px;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    flex-shrink: 0;
    cursor: pointer;
    transition: width 0.35s ease;

    &.is-expanded {
      width: 544px;
    }

    &.is-collapsed {
      width: 304px;
    }

    .card-image-stack {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      z-index: 0;
    }

    .card-image {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      opacity: 0;
      transition: opacity 0.2s ease;

      &.is-active {
        opacity: 1;
      }
    }

    .card-gradient {
      position: absolute;
      inset: 0;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 46%, #000 100%);
      pointer-events: none;
      z-index: 1;
    }

    .card-info {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 2;
      padding: 24px;
      display: flex;
      flex-direction: column;
      gap: 8px;

      .card-tags {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;

        .danger-tag {
          height: 30px;
          padding: 4px 12px;
          border-radius: 1000px;
          background: #9b273a;
          color: #fff;
          font-size: 14px;
          font-weight: 500;
          line-height: 22px;
          white-space: nowrap;
        }

        .exchange-tag {
          display: flex;
          align-items: center;
          gap: 6px;
          height: 30px;
          padding: 4px 8px;
          border-radius: 1000px;
          border: 0.5px solid rgba(255, 255, 255, 0.4);
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(50px);
          color: #fff;
          font-size: 14px;
          font-weight: 500;
          line-height: 22px;
          max-width: 100%;

          .exchange-logo {
            width: 22px;
            height: 22px;
            border-radius: 1000px;
            object-fit: cover;
            flex-shrink: 0;
          }
        }
      }

      .card-title {
        width: 100%;
        margin: 0;
        color: #fff;
        font-size: 20px;
        font-weight: 600;
        line-height: 28px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: start;
      }

      .card-meta {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .meta-item {
          display: flex;
          align-items: center;
          gap: 4px;
          color: #8c8f97;
          font-size: 14px;
          font-weight: 400;
          line-height: 22px;
          min-width: 0;

          span {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .meta-icon {
            // width: 16px;
            height: 16px;
            flex-shrink: 0;
          }

          &.is-time {
            font-size: 12px;
            line-height: 18px;
          }
        }
      }
    }

    .slider-dots {
      position: absolute;
      right: 12px;
      bottom: 18px;
      z-index: 3;
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 10px 12px;
      border-radius: 999px;
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transition: opacity 0.2s ease;

      [dir='rtl'] & {
        right: auto;
        left: 12px;
      }

      &.is-visible {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
      }

      .dot {
        width: 10px;
        height: 10px;
        border-radius: 5px;
        border: none;
        padding: 0;
        background: rgba(255, 255, 255, 0.3);
        cursor: pointer;
        transition:
          width 0.2s ease,
          background-color 0.2s ease;

        &.active {
          width: 27px;
          background: #fff;
        }
      }
    }
  }
}

@media screen and (max-width: 800px) {
  .exposure {
    width: 800px;
    min-width: 800px;
    max-width: none;

    .exposure-viewport {
      overflow-x: auto;

      .exposure-track {
        transform: none !important;
        width: max-content;
      }

      .exposure-page {
        width: max-content;
      }
    }

    .exposure-card {
      &.is-expanded {
        width: 362.67px;
      }

      &.is-collapsed {
        width: 202.67px;
      }
    }
  }
}

@media screen and (max-width: 1200px) and (min-width: 801px) {
  .exposure {
    width: calc(100% - 48px);
    margin: 0 auto;

    .exposure-card {
      &.is-expanded {
        width: calc((100% - 48px) * 544 / 1152);
      }

      &.is-collapsed {
        width: calc((100% - 48px) * 304 / 1152);
      }
    }
  }
}
</style>
